import { useEffect, useState } from "react";
import type { AttendeeSession } from "@verity-connect/contracts";
import { refusalText } from "./sign-in-doors";
import { emitAttendeeStateChanged, useAttendeeStateEpoch } from "./cross-device-state";

const LAST_SESSION_KEY = "wvcea-attendee-last-session-v1";

export type AttendeeAccountState =
  | { status: "loading" }
  | { status: "signed-out" }
  | { status: "signed-in"; session: AttendeeSession }
  | { status: "error"; message: string };

/** What asking the server for the session produced. */
export type SessionProbe =
  /** No answer at all — a dropped connection, no signal, aeroplane mode. */
  | { kind: "unreachable" }
  /** The server answered. Its answer is the truth, whatever it says. */
  | { kind: "answered"; status: number; session: AttendeeSession | null };

export type AccountDecision =
  /** Fall back to whatever this device remembers. The only route to the cache. */
  | { use: "cached" }
  | { use: "session"; session: AttendeeSession }
  | { use: "signed-out" }
  /** The server is faulty. Say so; sign nobody in; leave the cache alone. */
  | { use: "fault" };

/**
 * The one rule about when a remembered session may stand in for a live one.
 *
 * Written as a function of its own so it can be pinned down by tests, because getting it
 * wrong is not visible from the outside: it looks exactly like a working application.
 *
 * A refusing status and a dead network used to arrive in the same place, so any failure
 * reached the cache. A 503 from the attendee route then put a teacher back inside an
 * application the server had never signed her into, and a force-quit did not clear it
 * because the cache outlived the process. Only silence may reach the cache now.
 */
export function decideAccountState(probe: SessionProbe): AccountDecision {
  if (probe.kind === "unreachable") return { use: "cached" };
  // The session is finished and the server said so. The cache must go too, or the next
  // launch without signal would raise a session we have been told is dead.
  if (probe.status === 401 || probe.status === 403) return { use: "signed-out" };
  if (probe.status < 200 || probe.status > 299) return { use: "fault" };
  return probe.session ? { use: "session", session: probe.session } : { use: "signed-out" };
}

export function useAttendeeAccount() {
  const [state, setState] = useState<AttendeeAccountState>({ status: "loading" });
  const signal = useAttendeeStateEpoch();
  useEffect(() => {
    if (signal.reason === "signed-out" || signal.reason === "account-deleted") {
      clearLastSession();
      setState({ status: "signed-out" });
    }
    const controller = new AbortController();
    void (async () => {
      /**
       * The cached session is for one thing only: a phone with no signal.
       *
       * It used to stand in for any failure at all, because a refusing status and a dead
       * network arrived in the same `catch`. So a 503 from the attendee route — which is
       * what a broken account deletion answered — put a teacher back in a signed-in
       * application the server had never signed her into. It survived a force-quit,
       * because the cache did, and no amount of relaunching cleared it.
       *
       * Now only a transport failure may reach the cache. Any answer from the server is
       * the truth, including the ones that refuse.
       */
      let response: Response | null = null;
      let probe: SessionProbe;
      let unreachableReason: unknown = null;
      try {
        response = await fetch("/api/attendee/session", { signal: controller.signal, credentials: "same-origin" });
        if (controller.signal.aborted) return;
        probe = {
          kind: "answered",
          status: response.status,
          session: response.ok ? (await response.json() as AttendeeSession | null) : null,
        };
      } catch (reason: unknown) {
        if (controller.signal.aborted) return;
        unreachableReason = reason;
        probe = { kind: "unreachable" };
      }
      if (controller.signal.aborted) return;

      const decision = decideAccountState(probe);
      if (decision.use === "session") {
        writeLastSession(decision.session);
        setState({ status: "signed-in", session: decision.session });
        return;
      }
      if (decision.use === "signed-out") {
        clearLastSession();
        setState({ status: "signed-out" });
        return;
      }
      if (decision.use === "fault") {
        // Reported, not cached over. The application says it cannot tell rather than
        // pretending somebody is signed in.
        setState({ status: "error", message: response ? await responseMessage(response) : message(null) });
        return;
      }
      const saved = readLastSession();
      setState(saved ? { status: "signed-in", session: saved } : { status: "error", message: message(unreachableReason) });
    })();
    return () => controller.abort();
  }, [signal.epoch]);

  const signOut = async () => {
    await fetch("/api/attendee/logout", { method: "POST", credentials: "same-origin" });
    clearLastSession();
    window.dispatchEvent(new Event("wvcea:attendee-signed-out"));
    emitAttendeeStateChanged("signed-out");
    setState({ status: "signed-out" });
  };
  return { state, signOut };
}

/**
 * Both halves of a refusal. Every one now carries what happened and what to do next,
 * and showing only the first half was the old failure this replaces.
 */
async function responseMessage(response: Response): Promise<string> {
  return refusalText(response);
}
function message(reason: unknown): string { return reason instanceof Error ? reason.message : "Attendee account access could not be loaded."; }

function readLastSession(): AttendeeSession | null {
  try {
    const value = localStorage.getItem(LAST_SESSION_KEY);
    if (!value) return null;
    const session = JSON.parse(value) as AttendeeSession;
    if (!session || typeof session.identityId !== "string" || typeof session.expiresAt !== "string" || Date.parse(session.expiresAt) <= Date.now()) {
      clearLastSession();
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

function writeLastSession(session: AttendeeSession): void {
  try { localStorage.setItem(LAST_SESSION_KEY, JSON.stringify(session)); } catch { /* The live session still works when browser storage is unavailable. */ }
}

function clearLastSession(): void {
  try { localStorage.removeItem(LAST_SESSION_KEY); } catch { /* There is no local cache to clear when storage is unavailable. */ }
}
